import { useState } from "react";
import { api, ApiError } from "../api/client";
import { useQuery } from "../api/useQuery";
import { useAuth } from "../auth/AuthContext";
import { LazyMap } from "../components/LazyMap";
import { fmtDistance } from "../components/format";
import { parseGeoFile, type GeoKind, type ImportedGeometry } from "../components/geoImport";
import type { GeoRoute, GeoTerritory } from "../api/types";

type Selection =
  | { kind: "territory"; id: string }
  | { kind: "route"; id: string }
  | { kind: "new-territory" }
  | { kind: "new-route"; territoryId: string }
  | null;

/** Территории и маршруты: список слева, карта и форма справа. */
export function GeoPage() {
  const { me } = useAuth();
  const { data, error, loading, reload } = useQuery<{ items: GeoTerritory[] }>("/api/v1/geo");
  const [sel, setSel] = useState<Selection>(null);

  const items = data?.items ?? [];
  const canAddTerritory = me?.role === "admin" || me?.role === "director";
  const routeById = (id: string) => {
    for (const t of items) {
      const r = t.routes.find((x) => x.id === id);
      if (r) return { territory: t, route: r };
    }
    return null;
  };

  const done = () => { reload(); };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Территории и маршруты</h2>
          {data && <p className="muted small">
            {items.length} территорий · {items.reduce((n, t) => n + t.routes.filter((r) => r.is_active).length, 0)} действующих маршрутов
          </p>}
        </div>
        <div className="filters" style={{ margin: 0 }}>
          {canAddTerritory && <button className="secondary" onClick={() => setSel({ kind: "new-territory" })}>Новая территория</button>}
          {items[0] && <button onClick={() => setSel({ kind: "new-route", territoryId: items[0]!.id })}>Новый маршрут</button>}
        </div>
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      {loading && !data && <p className="notice">Загрузка…</p>}

      <div className="split">
        <div className="split-list" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {items.map((t) => (
            <div key={t.id}>
              <div className="section-title">{t.code}</div>
              <div className="list">
                <button className={`item${sel?.kind === "territory" && sel.id === t.id ? " selected" : ""}`}
                  onClick={() => setSel({ kind: "territory", id: t.id })}>
                  <span className="item-body">
                    <span className="item-title">{t.name}</span>
                    <span className="item-text">{t.boundary ? "граница загружена" : "граница не загружена"}</span>
                  </span>
                </button>
                {t.routes.map((r) => (
                  <button key={r.id} className={`item${sel?.kind === "route" && sel.id === r.id ? " selected" : ""}`}
                    onClick={() => setSel({ kind: "route", id: r.id })} style={{ paddingLeft: 32 }}>
                    <span className="item-body">
                      <span className="item-head">
                        <span className={r.is_active ? "" : "muted"}>{r.name}</span>
                        {!r.is_active && <span className="badge">выключен</span>}
                      </span>
                      <span className="item-text">
                        {r.length_m !== null ? fmtDistance(r.length_m) : "линия не загружена"} · меток: {r.tag_count}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {data && items.length === 0 && <div className="card empty">Территорий пока нет</div>}
        </div>

        <div className="split-detail">
          {sel === null && <div className="card empty">Выберите территорию или маршрут</div>}
          {sel?.kind === "territory" && (() => {
            const t = items.find((x) => x.id === sel.id);
            return t && <TerritoryEditor key={t.id} territory={t} onSaved={done} />;
          })()}
          {sel?.kind === "new-territory" && (
            <TerritoryEditor key="new" territory={null} onSaved={(id) => { done(); setSel({ kind: "territory", id }); }} />
          )}
          {sel?.kind === "route" && (() => {
            const found = routeById(sel.id);
            return found && <RouteEditor key={found.route.id} territories={items} territory={found.territory} route={found.route} onSaved={done} />;
          })()}
          {sel?.kind === "new-route" && (() => {
            const t = items.find((x) => x.id === sel.territoryId) ?? items[0];
            return t && <RouteEditor key="new" territories={items} territory={t}
              route={null} onSaved={(id) => { done(); setSel({ kind: "route", id }); }} />;
          })()}
        </div>
      </div>
    </>
  );
}

function TerritoryEditor({ territory, onSaved }: { territory: GeoTerritory | null; onSaved: (id: string) => void }) {
  const [code, setCode] = useState(territory?.code ?? "");
  const [name, setName] = useState(territory?.name ?? "");
  const [imported, setImported] = useState<ImportedGeometry | null>(null);
  const editable = territory === null || territory.can_edit;
  const save = useSave();

  const boundary = imported?.geometry ?? territory?.boundary ?? null;
  const routes = (territory?.routes ?? []).filter((r) => r.is_active && r.path).map((r) => r.path);

  return (
    <section className="card" aria-label="Территория">
      <h3 style={{ fontSize: 22 }}>{territory ? territory.name : "Новая территория"}</h3>
      <MapBox polygons={boundary ? [boundary] : []} routes={routes} empty="Граница не загружена" />

      {editable ? (
        <form style={formStyle} onSubmit={(e) => {
          e.preventDefault();
          const body = { code, name, boundary: imported?.geometry ?? null };
          void save.run(async () => territory
            ? (await api.put(`/api/v1/territories/${territory.id}`, { name, boundary: body.boundary }), territory.id)
            : (await api.post<{ id: string }>("/api/v1/territories", body)).id, onSaved);
        }}>
          {save.error && <div className="error" role="alert">{save.error}</div>}
          {!territory && (
            <label className="field">Код (латиницей, для служебных команд)
              <input required pattern="[a-z0-9_-]+" value={code} onChange={(e) => setCode(e.target.value.toLowerCase())} />
            </label>
          )}
          <label className="field">Название
            <input required value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <FileImport kind="polygon" label={territory?.boundary ? "Заменить границу" : "Граница"} onImported={setImported} />
          <div className="filters" style={{ margin: 0 }}>
            <button type="submit" disabled={save.busy || (!territory && !imported)}>{territory ? "Сохранить" : "Завести территорию"}</button>
          </div>
        </form>
      ) : (
        <p className="muted small">Границы и названия территорий меняют администратор и директор. Маршруты этой территории вы можете вести сами.</p>
      )}
    </section>
  );
}

function RouteEditor({ territories, territory, route, onSaved }: {
  territories: GeoTerritory[]; territory: GeoTerritory; route: GeoRoute | null; onSaved: (id: string) => void;
}) {
  const [territoryId, setTerritoryId] = useState(territory.id);
  const [name, setName] = useState(route?.name ?? "");
  const [active, setActive] = useState(route?.is_active ?? true);
  const [imported, setImported] = useState<ImportedGeometry | null>(null);
  const save = useSave();

  const current = territories.find((t) => t.id === territoryId) ?? territory;
  const path = imported?.geometry ?? route?.path ?? null;

  return (
    <section className="card" aria-label="Маршрут">
      <div className="muted small">{current.name}</div>
      <h3 style={{ fontSize: 22, marginTop: 2 }}>{route ? route.name : "Новый маршрут"}</h3>
      <MapBox polygons={current.boundary ? [current.boundary] : []} routes={path ? [path] : []} empty="Линия маршрута не загружена" />

      <form style={formStyle} onSubmit={(e) => {
        e.preventDefault();
        void save.run(async () => route
          ? (await api.put(`/api/v1/routes/${route.id}`, { name, is_active: active, path: imported?.geometry ?? null }), route.id)
          : (await api.post<{ id: string }>("/api/v1/routes", { territory_id: territoryId, name, path: imported?.geometry ?? null })).id,
        onSaved);
      }}>
        {save.error && <div className="error" role="alert">{save.error}</div>}
        {!route && territories.length > 1 && (
          <label className="field">Территория
            <select value={territoryId} onChange={(e) => setTerritoryId(e.target.value)}>
              {territories.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        )}
        <label className="field">Название
          <input required value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <FileImport kind="line" label={route?.path ? "Заменить линию" : "Линия маршрута"} onImported={setImported} />
        {route && (
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} style={{ minHeight: 0 }} />
            Действующий — выключенный маршрут не предлагается инспекторам, но остаётся в журнале обходов
          </label>
        )}
        {route && <p className="muted small" style={{ margin: 0 }}>Контрольных точек на маршруте: {route.tag_count}</p>}
        <div className="filters" style={{ margin: 0 }}>
          <button type="submit" disabled={save.busy || (!route && !imported)}>{route ? "Сохранить" : "Завести маршрут"}</button>
        </div>
      </form>
    </section>
  );
}

/** Выбор файла с разбором прямо в браузере и подсказкой, что нашлось. */
function FileImport({ kind, label, onImported }: { kind: GeoKind; label: string; onImported: (g: ImportedGeometry | null) => void }) {
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  return (
    <label className="field">{label} — файл GeoJSON, KML или GPX
      <input type="file" accept=".geojson,.json,.kml,.gpx" onChange={(e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        parseGeoFile(file, kind).then(
          (g) => { setStatus({ ok: true, text: `${file.name}: ${g.summary}` }); onImported(g); },
          (err: Error) => { setStatus({ ok: false, text: err.message }); onImported(null); },
        );
      }} />
      {status && <span className="small" style={{ color: status.ok ? "var(--accent)" : "var(--danger)" }}>{status.text}</span>}
    </label>
  );
}

function MapBox({ polygons, routes, empty }: { polygons: unknown[]; routes: unknown[]; empty: string }) {
  return (
    <div style={{ margin: "12px 0 16px" }}>
      <div style={{ borderRadius: 12, overflow: "hidden" }}>
        <LazyMap polygons={polygons} routes={routes} height={320} />
      </div>
      {!polygons.length && !routes.length && <p className="muted small">{empty}</p>}
    </div>
  );
}

function useSave() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return {
    busy, error,
    async run(fn: () => Promise<string>, onDone: (id: string) => void) {
      setBusy(true);
      setError(null);
      try {
        onDone(await fn());
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Не удалось сохранить");
      } finally {
        setBusy(false);
      }
    },
  };
}

const formStyle = { display: "flex", flexDirection: "column" as const, gap: 14 };
